import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import {
  AssessmentField,
  FormulaField,
  HeaderField,
  PaymentMethodField,
  TrainingField,
} from 'src/models/assessment/_types';
import { Exercise } from 'src/models/exercise/_types';
import { fetchExercise } from 'src/models/exercise/exercise';
import { defaultStats } from 'src/models/user/utils';
import { ExerciseAutocomplete } from 'src/shared/Forms/Selects/ExerciseAutocomplete';

import { AssessmentFormValues } from '../AssessmentNewPage';

interface AssessmentQuestionFieldProps {
  field: AssessmentField;
  index: number;
  numFields: number;
  showConditional: boolean;
  onDelete: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
}

const getDisplayType = (field: AssessmentField, t: any, tField: any) => {
  switch (field.type) {
    case 'text':
      return tField('shortAnswer');
    case 'textarea':
      return tField('longAnswer');
    case 'checkbox':
      return tField('checkboxGroup');
    case 'dropdown':
      return tField('dropdown');
    case 'toggle':
      return tField('toggle');
    case 'header':
      return tField('header');
    case 'instructions':
      return tField('instructionsDetails');
    case 'upload':
      return t('trainer.progress.full');
    case 'file':
      return tField('fileUpload');
    case 'gender':
      return tField('gender');
    case 'height':
      return tField('heightInches');
    case 'dob':
      return tField('dateOfBirth');
    case 'phone_number':
      return tField('phoneNumber');
    case 'street_address':
      return tField('streetAddress');
    case 'city':
      return tField('city');
    case 'state':
      return tField('state');
    case 'zip_code':
      return tField('zipCode');
    case 'rep_max':
      return `Rep Max for ${t('exercise.one')}`;
    case 'waiver':
      return tField('waiver');
    case 'formula':
      return tField('formulaField');
    case 'password':
      return tField('password');
    case 'credit_card':
      return tField('paymentMethod');
    case 'email':
      return tField('emailUpdateCompleters', {
        assessmentLower: t('assessment.one').toLowerCase(),
      });
    default:
      if (
        field.type === 'system' ||
        field.type === 'profile' ||
        field.type === 'training'
      ) {
        return field.label || '';
      }
      return field.type;
  }
};

const getPlaceholder = (field: AssessmentField, tField: any) => {
  switch (field.type) {
    case 'text':
      return tField('placeholder.shortAnswer');
    case 'textarea':
      return tField('placeholder.textarea');
    case 'checkbox':
      return tField('placeholder.checkbox');
    case 'dropdown':
      return tField('placeholder.dropdown');
    case 'toggle':
      return tField('placeholder.toggle');
    case 'header':
      return tField('placeholder.header');
    case 'instructions':
      return tField('placeholder.instructions');
    case 'upload':
      return tField('placeholder.photoVideo');
    case 'file':
      return tField('placeholder.fileUpload');
    case 'gender':
      return tField('placeholder.gender');
    case 'height':
      return tField('placeholder.height');
    case 'dob':
      return tField('placeholder.dateOfBirth');
    case 'phone_number':
      return tField('placeholder.phoneNumber');
    case 'street_address':
      return tField('placeholder.streetAddress');
    case 'city':
      return tField('placeholder.city');
    case 'state':
      return tField('placeholder.state');
    case 'zip_code':
      return tField('placeholder.zipCode');
    case 'rep_max':
      return tField('placeholder.repMax');
    case 'waiver':
      return tField('placeholder.waiver');
    case 'formula':
      return tField('placeholder.formula');
    case 'password':
      return tField('placeholder.password');
    case 'email':
      return tField('placeholder.email');
    case 'credit_card':
      return tField('placeholder.creditCard');
    default:
      return '';
  }
};

const getOptionPlaceholder = (type: string, tField: any) => {
  if (type === 'checkbox') return tField('optionPlaceholder.checkbox');
  if (type === 'dropdown') return tField('optionPlaceholder.dropdown');
  return '';
};

const hasOptions = (field: AssessmentField) => {
  return ['dropdown', 'checkbox', 'toggle'].includes(field.type);
};

const isQuestion = (field: AssessmentField) => {
  const nonQuestionTypes = [
    'credit_card',
    'waiver',
    'header',
    'instructions',
    'upload',
    'dob',
    'height',
    'gender',
    'notes',
    'goals',
    'injuries',
    'equipment',
    'profile',
    'training',
  ];
  return !nonQuestionTypes.includes(field.type);
};

export const AssessmentQuestionField: React.FC<
  AssessmentQuestionFieldProps
> = ({
  field,
  index,
  numFields,
  showConditional,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessmentQuestionField',
  });
  const { t: tGlobal } = useTranslation();
  const { values, setFieldValue } = useFormikContext<AssessmentFormValues>();
  const { sessionTrainer, user: currentUser } = useSession();

  const [bulkFieldOptions, setBulkFieldOptions] = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [showExerciseSelect, setShowExerciseSelect] = useState(false);

  const isFirst = index === 0;
  const isLast = index === numFields - 1;
  const displayIndex = index + 1;
  const displayType = getDisplayType(field, tGlobal, t);
  const placeholder = getPlaceholder(field, t);
  const optionsExist = hasOptions(field);
  const isQ = isQuestion(field);

  // Get metric options (reportable stats) - matches Ember's reportable computed property
  // Uses sessionTrainer (currentWhiteLabeler) or user (currentUser) like Ember
  // In Ember: value = r.get('stat'), name = r.get('label')
  // So value should be stat (lowercase like 'weight'), label should be display name (like 'Weight')
  const metricOptions = (() => {
    const user = sessionTrainer || currentUser;
    if (!user) return [];

    // Start with default stats from defaultStats (matches Ember's defaultReportable)
    // Filter out height (like Ember)
    let stats = defaultStats
      .filter((stat) => stat.stat !== 'height')
      .map((stat) => ({
        stat: stat.stat,
        label: stat.label,
      }));

    // Add healthInfoMetrics if they exist (custom metrics)
    // Custom metrics don't have stat, so convert label to stat format (like Ember's updateHealthFields)
    if (user.healthInfoMetrics && user.healthInfoMetrics.length > 0) {
      const customMetrics = user.healthInfoMetrics.map((label) => ({
        stat: label
          .toLowerCase()
          .replace(/[\s_-]+/g, '_')
          .replace(/[\W]/g, ''),
        label: label,
      }));
      stats = stats.concat(customMetrics);
    }

    // Filter out defaultHealthMetrics (these are the ones we DON'T want to include)
    if (user.defaultHealthMetrics && user.defaultHealthMetrics.length > 0) {
      stats = stats.filter(
        (stat) => !user.defaultHealthMetrics?.includes(stat.label)
      );
    }

    // Apply measurementsOrder if it exists (reverse order like Ember)
    if (user.measurementsOrder && user.measurementsOrder.length > 0) {
      const orderedStats: typeof stats = [];
      const remainingStats = [...stats];

      // Process in reverse order (like Ember: .slice().reverse())
      user.measurementsOrder
        .slice()
        .reverse()
        .forEach((label) => {
          const index = remainingStats.findIndex((s) => s.label === label);
          if (index !== -1) {
            orderedStats.unshift(remainingStats[index]);
            remainingStats.splice(index, 1);
          }
        });

      // Add remaining stats that weren't in the order
      stats = [...orderedStats, ...remainingStats];
    }

    // Convert to options format: value = stat, label = display name
    // Matches Ember's metricOptions: { value: r.get('stat'), name: r.get('label') }
    return stats.map((s) => ({
      value: s.stat,
      label: s.label,
    }));
  })();

  const weightMetrics = [
    { label: 'lbs', value: false },
    { label: 'kg', value: true },
  ];

  const paymentMethodTypes = [
    { value: 'card', label: 'Card' },
    { value: 'bank', label: 'Bank' },
  ];

  const pointOptions: number[] = [];
  for (let i = -20; i <= 100; i++) {
    pointOptions.push(i);
  }

  // Public assessment link
  const publicAssessmentLink = externalLinks.v2Url(
    `/assessment/${values.id || ''}`
  );

  // Check if previous field has answer (for conditional display)
  const hasConditional = (() => {
    if (index <= 0) return false;
    const prevField = values.fields[index - 1];
    return !!(prevField as any).answer;
  })();

  // Get current options or create empty array
  const getFieldOptions = () => {
    if ('options' in field) {
      return field.options || [];
    }
    return [];
  };

  const options = getFieldOptions();

  // Can have points (for scored assessments)
  const canHavePoints = field.type === 'text';
  const scored = values.scored || false;

  // Sync bulk options with field options
  useEffect(() => {
    if (bulkMode && options.length > 0) {
      const bulkText = options.map((o) => o.label).join(',');
      setBulkFieldOptions(bulkText);
    }
  }, [bulkMode, options]);

  const handleBulkOptionsChange = (value: string) => {
    setBulkFieldOptions(value);
    const trimmedValue = value.replace(/, | ,/g, ',').trim();

    if (trimmedValue && 'options' in field) {
      const newOptions = trimmedValue
        .split(',')
        .filter(Boolean)
        .map((label) => ({ label, default: false }));

      updateField({ options: newOptions } as any);
    }
  };

  const toggleBulkOptions = () => {
    const newBulkMode = !bulkMode;
    setBulkMode(newBulkMode);

    if (newBulkMode && options.length > 0) {
      const bulkText = options.map((o) => o.label).join(',');
      setBulkFieldOptions(bulkText);
    } else if (!newBulkMode && bulkFieldOptions) {
      const newOptions = bulkFieldOptions
        .split(',')
        .filter(Boolean)
        .map((label) => ({ label, default: false }));
      updateField({ options: newOptions } as any);
    }
  };

  const updateField = (updates: Partial<AssessmentField>) => {
    const updatedFields = [...values.fields];
    updatedFields[index] = { ...field, ...updates };
    setFieldValue('fields', updatedFields);
  };

  const addOption = () => {
    if ('options' in field) {
      const newOptions = [
        ...(field.options || []),
        { label: '', default: false, points: 0 },
      ];
      updateField({ options: newOptions } as any);
    }
  };

  const deleteOption = (optionIndex: number) => {
    if ('options' in field) {
      const newOptions = field.options.filter((_, i) => i !== optionIndex);
      updateField({ options: newOptions } as any);
    }
  };

  const updateOption = (
    optionIndex: number,
    updates: { label?: string; points?: number; default?: boolean }
  ) => {
    if ('options' in field) {
      const newOptions = field.options.map((o, i) =>
        i === optionIndex ? { ...o, ...updates } : o
      );
      updateField({ options: newOptions } as any);
    }
  };

  const makeDefault = (optionIndex: number) => {
    if ('options' in field) {
      const newOptions = field.options.map((o, i) => ({
        ...o,
        default: i === optionIndex,
      }));
      updateField({ options: newOptions } as any);
    }
  };

  const handleExerciseSelect = (exercise: Exercise) => {
    if (exercise.id) {
      updateField({ exercise: Number(exercise.id) } as any);
      setShowExerciseSelect(false);
    }
  };

  const handleAddExerciseEmbed = async (exercise: Exercise) => {
    if (exercise.videoEmbedUrl) {
      const embedHtml = `<div class="flex-video widescreen"><iframe width="560" height="315" src="${exercise.videoEmbedUrl}" frameborder="0" allowfullscreen autoplay=false autostart=false></iframe></div>`;
      const newLabel = field.label + embedHtml;
      updateField({ label: newLabel });
    }
    setShowExerciseSelect(false);
  };

  const handleCheckRequired = () => {
    if (hasConditional) {
      updateField({ required: false });
    } else {
      updateField({ required: !field.required });
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        mb: 2,
      }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 11 }}>
          <Box sx={{ mb: 2 }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              {!isFirst && (
                <Tooltip title={t('moveQuestionUp')}>
                  <IconButton
                    onClick={() => onMoveUp(index)}
                    color="primary"
                    size="small"
                  >
                    <Icon icon={icon({ name: 'arrow-up' })} size="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!isLast && (
                <Tooltip title={t('moveQuestionDown')}>
                  <IconButton
                    onClick={() => onMoveDown(index)}
                    color="primary"
                    size="small"
                  >
                    <Icon icon={icon({ name: 'arrow-down' })} size="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Typography variant="body1" fontWeight="bold">
                {field.type === 'header' ? (
                  <>
                    {displayIndex}. {displayType} {t('section')}
                  </>
                ) : (
                  <>
                    {displayIndex}.
                    {field.type === 'system' ||
                    field.type === 'profile' ||
                    field.type === 'training' ? (
                      <>
                        {' '}
                        {field.label} {t('field')}
                      </>
                    ) : (
                      <>
                        {' '}
                        {displayType} {isQ ? t('question') : t('section')}
                      </>
                    )}
                  </>
                )}
              </Typography>

              {field.type === 'formula' && (
                <Tooltip title={t('supportArticle')} arrow>
                  <Link
                    href="https://www.exercise.com/support/assessment-formula-fields/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'inline-flex' }}
                  >
                    <Icon
                      icon={icon({ name: 'question-circle' })}
                      size="small"
                    />
                  </Link>
                </Tooltip>
              )}

              <Tooltip title={t('deleteField')}>
                <IconButton
                  onClick={() => onDelete(index)}
                  color="error"
                  size="small"
                  sx={{ ml: 'auto' }}
                >
                  <Icon icon={icon({ name: 'trash' })} size="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Publish to activity feed for system/training fields */}
            {(field.type === 'system' || field.type === 'training') && (
              <Checkbox
                checked={(field as any).publish || false}
                onChange={(e) =>
                  updateField({ publish: e.target.checked } as any)
                }
                label={t('publishToActivityFeed')}
                size="small"
                sx={{ mb: 2 }}
              />
            )}

            {/* Conditional question notice */}
            {showConditional && hasConditional && (
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', mb: 2, color: 'text.secondary' }}
              >
                {t('conditionalQuestionNotice')}
              </Typography>
            )}

            {/* Field-specific inputs */}
            {field.type === 'instructions' ? (
              <TextField
                label=""
                value={field.label}
                onChange={(e) => updateField({ label: e.target.value })}
                placeholder={placeholder}
                multiline
                minRows={3}
                sx={{ mb: 2 }}
              />
            ) : field.type === 'waiver' ? (
              <>
                <TextField
                  label=""
                  value={field.label}
                  onChange={(e) => updateField({ label: e.target.value })}
                  placeholder={placeholder}
                  multiline
                  minRows={3}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label={t('waiverCheckboxText')}
                  value={(field as any).option || ''}
                  onChange={(e) =>
                    updateField({ option: e.target.value } as any)
                  }
                  sx={{ mb: 2 }}
                />
              </>
            ) : (field as TrainingField).stat === 'weight' ? (
              <Grid
                container
                spacing={2}
                sx={{ display: 'flex', alignItems: 'flex-end' }}
              >
                <Grid size={{ xs: 12, md: 10 }}>
                  <TextField
                    label=""
                    value={field.label}
                    onChange={(e) => updateField({ label: e.target.value })}
                    placeholder={placeholder}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                  <TextField
                    label={t('default')}
                    value={(field as any).metric ? 'kg' : 'lbs'}
                    onChange={(e) =>
                      updateField({ metric: e.target.value === 'kg' } as any)
                    }
                    options={weightMetrics.map((m) => ({
                      value: m.value ? 'kg' : 'lbs',
                      label: m.label,
                    }))}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
            ) : field.type === 'rep_max' ? (
              <>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <ExerciseAutocomplete
                      label=""
                      placeholder={t('selectExerciseForRepMax', {
                        exercise: tGlobal('exercise.one'),
                      })}
                      value={(field as any).exercise}
                      onChange={(exercise) => {
                        if (exercise) {
                          handleExerciseSelect(exercise);
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label={t('numberOfReps')}
                      value={(field as any).reps || ''}
                      onChange={(e) =>
                        updateField({ reps: e.target.value } as any)
                      }
                      placeholder={t('enterReps')}
                      type="number"
                      sx={{ mb: 0.5 }}
                      helperText={t('numberOfRepsHelper')}
                    />
                  </Grid>
                </Grid>
                <TextField
                  label={t('instructions')}
                  value={field.label}
                  onChange={(e) => updateField({ label: e.target.value })}
                  placeholder={placeholder}
                  sx={{ mb: 2 }}
                />
              </>
            ) : (
              <TextField
                label=""
                value={field.label}
                onChange={(e) => updateField({ label: e.target.value })}
                placeholder={placeholder}
                multiline={field.type === 'textarea'}
                sx={{ mb: 2 }}
              />
            )}

            {/* Payment Method Types */}
            {field.type === 'credit_card' && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label={t('paymentMethodTypes')}
                  value={
                    (field as PaymentMethodField).paymentMethodTypes || ['card']
                  }
                  onChange={(e) =>
                    updateField({ paymentMethodTypes: e.target.value } as any)
                  }
                  options={paymentMethodTypes.map((type) => ({
                    value: type.value,
                    label: type.label,
                  }))}
                  sx={{ mb: 2 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mt: 1 }}
                >
                  <i>{t('bankType')}</i>{' '}
                  {t('typeOnlySupportedForPublic', { type: t('bankType') })}{' '}
                  <Link
                    href={publicAssessmentLink}
                    target="_blank"
                    sx={{ fontSize: 'inherit' }}
                  >
                    {t('typeOnlySupportedForPublicLinkText', {
                      assessmentLower: tGlobal('assessment.one').toLowerCase(),
                    })}
                  </Link>
                  .
                </Typography>
              </Box>
            )}

            {/* Formula Field */}
            {field.type === 'formula' && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label={t('formula')}
                  value={(field as FormulaField).formula || ''}
                  onChange={(e) => updateField({ formula: e.target.value })}
                  placeholder={t('enterFormulaHere')}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('saveResultAsCustomMeasurement', {
                          measurement: tGlobal('measurement.one'),
                        })}
                        <Tooltip
                          title={t('saveResultAsCustomMeasurementTooltip', {
                            measurementLower:
                              tGlobal('measurement.one').toLowerCase(),
                          })}
                        >
                          <Icon
                            icon={icon({ name: 'question-circle' })}
                            sx={{ ml: 0.5 }}
                            size="small"
                          />
                        </Tooltip>
                      </Typography>
                      <TextField
                        value={(field as FormulaField).formulaStat || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // If empty string, set to undefined to clear selection
                          updateField({
                            formulaStat: value === '' ? undefined : value,
                          });
                        }}
                        options={[
                          {
                            value: '',
                            label: t('select'),
                          },
                          ...metricOptions,
                        ]}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    {(field as FormulaField).formulaStat === 'weight' && (
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          label={t('default')}
                          value={
                            (field as FormulaField).default !== undefined
                              ? (field as FormulaField).default
                                ? 'true'
                                : 'false'
                              : 'false'
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            updateField({
                              default: value === 'true',
                            });
                          }}
                          options={weightMetrics.map((m) => ({
                            value: m.value ? 'true' : 'false',
                            label: m.label,
                          }))}
                          sx={{ mb: 2 }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Box>
            )}

            {/* Upload Field - tie to measurement */}
            {field.type === 'upload' && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography>
                    {t('tieUploadToMeasurement', {
                      measurement: tGlobal('measurement.one'),
                    })}
                  </Typography>
                  <Tooltip
                    title={t('tieUploadToMeasurementTooltip', {
                      measurementLower:
                        tGlobal('measurement.one').toLowerCase(),
                      assessmentLower: tGlobal('assessment.one').toLowerCase(),
                    })}
                  >
                    <Icon
                      icon={icon({ name: 'question-circle' })}
                      size="small"
                    />
                  </Tooltip>
                </Box>
                <TextField
                  value={(field as any).stat || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // If empty string, set to undefined to clear selection
                    updateField({
                      stat: value === '' ? undefined : value,
                    } as any);
                  }}
                  options={[
                    {
                      value: '',
                      label: `No ${tGlobal('measurement.one')}`,
                    },
                    ...metricOptions,
                  ]}
                  sx={{ mb: 2 }}
                />
              </Box>
            )}

            {/* Instructions field - add exercise video */}
            {field.type === 'instructions' && (
              <Box sx={{ mb: 2 }}>
                {showExerciseSelect ? (
                  <ExerciseAutocomplete
                    label=""
                    placeholder={t('selectExerciseVideoToAdd', {
                      exercise: tGlobal('exercise.one'),
                    })}
                    value={(field as any).exercise}
                    onChange={async (ex) => {
                      if (ex) {
                        const res = await fetchExercise({ id: ex.id });
                        handleAddExerciseEmbed(res);
                      }
                    }}
                  />
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => setShowExerciseSelect(true)}
                    size="small"
                  >
                    {t('addExerciseVideo', {
                      exercise: tGlobal('exercise.one'),
                    })}
                  </Button>
                )}
              </Box>
            )}

            {/* Conditional answer */}
            {showConditional && (
              <Box sx={{ mb: 2 }}>
                <Tooltip
                  title={`If the ${tGlobal(
                    'assessment.one'
                  ).toLowerCase()} response does not match what you enter here, the following question will not be revealed.`}
                >
                  <TextField
                    label={t('conditionalAnswer')}
                    value={(field as any).answer || ''}
                    onChange={(e) =>
                      updateField({ answer: e.target.value } as any)
                    }
                    placeholder={t('enterConditionalAnswer')}
                    fullWidth
                  />
                </Tooltip>
              </Box>
            )}

            {/* Header divider */}
            {field.type === 'header' && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 2 }} />
            )}

            {/* Points formula for scored assessments */}
            {field.type !== 'header' && canHavePoints && scored && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography variant="body2">
                    {t('formulaForPoints')}
                  </Typography>
                  <Tooltip title={t('overridePointsFormula')}>
                    <Link
                      href="https://www.exercise.com/support/assessment-formula-fields/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon
                        icon={icon({ name: 'question-circle' })}
                        size="small"
                      />
                    </Link>
                  </Tooltip>
                </Box>
                <TextField
                  value={(field as HeaderField).pointFormula || ''}
                  onChange={(e) =>
                    updateField({ pointFormula: e.target.value } as any)
                  }
                  placeholder="e.g. qv1 + qv2"
                  fullWidth
                />
              </Box>
            )}
          </Box>

          {/* Options for checkbox/dropdown/toggle */}
          {optionsExist && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {displayType} {t('options')}
                {field.type === 'toggle' ? ` ${t('firstOptionIsOff')}` : ''}:
              </Typography>

              {bulkMode ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{ cursor: 'pointer', color: 'primary.main', mb: 1 }}
                    onClick={toggleBulkOptions}
                  >
                    {t('addOptionsIndividually')}
                  </Typography>
                  <TextField
                    label=""
                    value={bulkFieldOptions}
                    onChange={(e) => handleBulkOptionsChange(e.target.value)}
                    placeholder={t('addCommaSeparatedOptions')}
                    multiline
                    minRows={3}
                    sx={{ mb: 2 }}
                  />
                </>
              ) : (
                <>
                  {field.type !== 'toggle' && (
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer', color: 'primary.main', mb: 2 }}
                      onClick={toggleBulkOptions}
                    >
                      {t('addOptionsInBulk')}
                    </Typography>
                  )}
                  <Stack spacing={1}>
                    {options.map((option, optionIndex) => (
                      <Box
                        key={optionIndex}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <TextField
                          value={option.label}
                          onChange={(e) =>
                            updateOption(optionIndex, { label: e.target.value })
                          }
                          placeholder={getOptionPlaceholder(field.type, t)}
                          sx={{ flex: 1 }}
                        />
                        {scored && (
                          <TextField
                            value={option.points || 0}
                            onChange={(e) =>
                              updateOption(optionIndex, {
                                points: Number(e.target.value),
                              })
                            }
                            type="number"
                            options={pointOptions.map((p) => ({
                              value: p,
                              label: String(p),
                            }))}
                            sx={{ width: 100 }}
                          />
                        )}
                        {field.type === 'dropdown' && (
                          <Checkbox
                            checked={option.default || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                makeDefault(optionIndex);
                              } else {
                                updateOption(optionIndex, { default: false });
                              }
                            }}
                            sx={{ p: 0 }}
                          />
                        )}
                        {field.type !== 'toggle' && (
                          <IconButton
                            onClick={() => deleteOption(optionIndex)}
                            color="error"
                            size="small"
                          >
                            <Icon icon={icon({ name: 'xmark' })} size="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    {field.type !== 'toggle' && (
                      <Button
                        variant="outlined"
                        onClick={addOption}
                        startIcon={
                          <Icon icon={icon({ name: 'plus' })} size="small" />
                        }
                        size="small"
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {t('option')}
                      </Button>
                    )}
                  </Stack>
                </>
              )}
            </Box>
          )}
        </Grid>

        {/* Required checkbox */}
        <Grid size={{ xs: 12, md: 1 }}>
          {!hasConditional && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Tooltip title={t('required')}>
                <Checkbox
                  checked={field.required || false}
                  onChange={handleCheckRequired}
                  sx={{ p: 0 }}
                />
              </Tooltip>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
